// FILE: timelineHeight.ts
// Purpose: Estimates chat row heights before ResizeObserver measurements arrive.
// Layer: Web chat virtualization utility
// Exports: message/work height estimators used by MessagesTimeline and browser tests

import type { TurnDiffFileChange } from "../types";
import { DEFAULT_CHAT_FONT_SIZE_PX, normalizeChatFontSizePx } from "../appSettings";
import { deriveDisplayedUserMessageState } from "../lib/displayedUserMessage";
import { getAppTypographyScale } from "../lib/appTypography";
import { buildTurnDiffTree, type TurnDiffTreeNode } from "../lib/turnDiffTree";
import { buildInlineTerminalContextText } from "./chat/userMessageTerminalContexts";
import {
  extractWorkEntryAttachmentImages,
  WORK_ENTRY_ATTACHMENT_PREVIEW_MAX_HEIGHT_PX,
} from "./chat/MessagesTimeline.logic";
import {
  getChatTranscriptAssistantCharWidthPx,
  getChatTranscriptLineHeightPx,
  getChatTranscriptUserCharWidthPx,
} from "./chat/chatTypography";

const ASSISTANT_CHARS_PER_LINE_FALLBACK = 72;
const USER_CHARS_PER_LINE_FALLBACK = 56;
const ASSISTANT_BASE_HEIGHT_PX = 78;
const USER_BASE_HEIGHT_PX = 96;
const USER_ATTACHMENT_THUMBNAIL_SIZE_PX = 60;
const USER_ATTACHMENT_THUMBNAIL_GAP_PX = 8;
const USER_ATTACHMENT_THUMBNAILS_PER_ROW = 4;
const USER_ATTACHMENT_ROW_MARGIN_BOTTOM_PX = 4;
/**
 * Mirrors the user bubble's CSS width: it spans the full chat column (`w-full`),
 * so the estimate must not shrink it the way the old right-aligned bubble did.
 */
const USER_BUBBLE_WIDTH_RATIO = 1;
const USER_BUBBLE_HORIZONTAL_PADDING_PX = 32;
const ASSISTANT_MESSAGE_HORIZONTAL_PADDING_PX = 8;
// SystemNoticeRow: `my-1.5` above and below plus the row wrapper's `pb-4`.
const SYSTEM_NOTICE_CHROME_HEIGHT_PX = 28;
const SYSTEM_NOTICE_LINE_HEIGHT_RATIO = 1.5;
// Its bot icon, the gap next to it and the row's own `px-1`.
const SYSTEM_NOTICE_HORIZONTAL_CHROME_PX = 30;
// The timestamp and the "Ver detalle" toggle share the notice's first line.
const SYSTEM_NOTICE_TRAILING_META_CHARS = 18;
const SYSTEM_NOTICE_CHAR_WIDTH_RATIO = 0.52;
const MIN_SYSTEM_NOTICE_CHARS_PER_LINE = 16;
const SYSTEM_NOTICE_CHARS_PER_LINE_FALLBACK = 80;
const MIN_USER_CHARS_PER_LINE = 4;
const MIN_ASSISTANT_CHARS_PER_LINE = 20;
const ASSISTANT_INLINE_CODE_WIDTH_MULTIPLIER = 1.2;
const ASSISTANT_INLINE_CODE_WRAP_OVERHEAD_CHARS = 2;
const INLINE_CODE_SPAN_REGEX = /`([^`\n]+)`/g;
const TURN_DIFF_SUMMARY_CHROME_HEIGHT_PX = 76;
const TURN_DIFF_TREE_ROW_HEIGHT_PX = 24;
const TURN_DIFF_TREE_ROW_GAP_PX = 2;
const WORK_GROUP_CHROME_HEIGHT_PX = 24;
const WORK_GROUP_HEADER_HEIGHT_PX = 20;
const WORK_ENTRY_ROW_HEIGHT_PX = 30;
const WORK_ENTRY_CHANGED_FILES_HEIGHT_PX = 24;
const WORK_ENTRY_GAP_PX = 2;
// Capture previews under a tool row. They render whether or not the row is
// expanded, so this height lands on the collapsed row too. Their widths are not
// known until the images load — a portrait screencap is narrow, a desktop one is
// wide — so the estimate assumes the wide case and wraps every two previews,
// which biases the row upward like the rest of this file.
const WORK_ENTRY_ATTACHMENT_BLOCK_MARGIN_TOP_PX = 6;
const WORK_ENTRY_ATTACHMENT_CAPTION_HEIGHT_PX = 18;
const WORK_ENTRY_ATTACHMENT_GAP_PX = 8;
const WORK_ENTRY_ATTACHMENT_PREVIEWS_PER_LINE = 2;
// Level 3: the detail panel a single tool row opens, capped the same way its CSS is.
const WORK_ENTRY_DETAIL_MARGIN_TOP_PX = 4;
const WORK_ENTRY_DETAIL_CHROME_HEIGHT_PX = 34;
const WORK_ENTRY_DETAIL_LINE_HEIGHT_PX = 17;
/** Kept in sync with the CSS max-height of the tool detail panel. */
export const WORK_ENTRY_DETAIL_MAX_HEIGHT_PX = 320;
const WORK_ENTRY_DETAIL_CHARS_PER_LINE = 96;
/** The "Open thread" pill row is taller than a text line but wraps like one. */
const SUBAGENT_THREAD_LINKS_LINE_EQUIVALENT = 2;
const INLINE_TOOL_PREVIEW_MARGIN_TOP_PX = 10;
const INLINE_TOOL_PREVIEW_ROW_HEIGHT_PX = 22;
const INLINE_TOOL_PREVIEW_ROW_GAP_PX = 1;
const INLINE_TOOL_PREVIEW_CONTAINER_CHROME_HEIGHT_PX = 0;
const changedFilesSummaryHeightCache = new WeakMap<
  ReadonlyArray<TurnDiffFileChange>,
  { collapsed?: number; expanded?: number }
>();

interface TimelineMessageHeightInput {
  role: "user" | "assistant" | "system";
  text: string;
  attachments?: ReadonlyArray<{ id: string }>;
  diffSummaryFiles?: ReadonlyArray<TurnDiffFileChange>;
  diffSummaryAllDirectoriesExpanded?: boolean;
  inlineToolEntries?: ReadonlyArray<TimelineWorkEntryHeightInput>;
  inlineToolExpanded?: boolean;
}

interface TimelineHeightEstimateLayout {
  timelineWidthPx: number | null;
  chatFontSizePx?: number;
}

interface TimelineWorkEntryHeightInput {
  tone: "thinking" | "tool" | "info" | "error";
  command?: string | null;
  detail?: string | null;
  changedFiles?: ReadonlyArray<string>;
  /** Real tool output shown by the level 3 detail panel. */
  output?: string | null;
  /**
   * Set on in-session subagent rows (Claude `Task`). Their detail panel adds a
   * status line and the prompt on top of the shared output block, so a row that
   * carries no output at all can still open into something.
   */
  subagentTask?: {
    prompt?: string | null | undefined;
    threadLinks?: ReadonlyArray<unknown> | null | undefined;
  } | null;
  /** True when this row's detail panel is open (level 3). */
  expanded?: boolean;
}

interface TimelineWorkGroupEstimateOptions {
  /** False renders the level 1 one-line summary; true renders the row list. */
  expanded: boolean;
  /** Legacy tail cap. Omit to render every row, which is what the 3-level chat does. */
  maxVisibleEntries?: number;
}

function estimateWrappedLineCount(text: string, charsPerLine: number): number {
  if (text.length === 0) return 1;

  // Avoid allocating via split for long logs; iterate once and count wrapped lines.
  let lines = 0;
  let currentLineLength = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (text.charCodeAt(index) === 10) {
      lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
      currentLineLength = 0;
      continue;
    }
    currentLineLength += 1;
  }

  lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
  return lines;
}

function isFinitePositiveNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function estimateCharsPerLineForUser(
  timelineWidthPx: number | null,
  chatFontSizePx: number,
): number {
  if (!isFinitePositiveNumber(timelineWidthPx)) return USER_CHARS_PER_LINE_FALLBACK;
  const bubbleWidthPx = timelineWidthPx * USER_BUBBLE_WIDTH_RATIO;
  const textWidthPx = Math.max(bubbleWidthPx - USER_BUBBLE_HORIZONTAL_PADDING_PX, 0);
  return Math.max(
    MIN_USER_CHARS_PER_LINE,
    Math.floor(textWidthPx / getChatTranscriptUserCharWidthPx(chatFontSizePx)),
  );
}

function estimateCharsPerLineForAssistant(
  timelineWidthPx: number | null,
  chatFontSizePx: number,
): number {
  if (!isFinitePositiveNumber(timelineWidthPx)) return ASSISTANT_CHARS_PER_LINE_FALLBACK;
  const textWidthPx = Math.max(timelineWidthPx - ASSISTANT_MESSAGE_HORIZONTAL_PADDING_PX, 0);
  return Math.max(
    MIN_ASSISTANT_CHARS_PER_LINE,
    Math.floor(textWidthPx / getChatTranscriptAssistantCharWidthPx(chatFontSizePx)),
  );
}

function estimateCharsPerLineForSystemNotice(
  timelineWidthPx: number | null,
  noticeFontSizePx: number,
): number {
  if (!isFinitePositiveNumber(timelineWidthPx)) return SYSTEM_NOTICE_CHARS_PER_LINE_FALLBACK;
  const textWidthPx = Math.max(timelineWidthPx - SYSTEM_NOTICE_HORIZONTAL_CHROME_PX, 0);
  return Math.max(
    MIN_SYSTEM_NOTICE_CHARS_PER_LINE,
    Math.floor(textWidthPx / (noticeFontSizePx * SYSTEM_NOTICE_CHAR_WIDTH_RATIO)),
  );
}

// Count the tree rows the diff summary will render before ResizeObserver corrects the real size.
function countVisibleTurnDiffTreeRows(
  nodes: ReadonlyArray<TurnDiffTreeNode>,
  allDirectoriesExpanded: boolean,
): number {
  let count = 0;
  for (const node of nodes) {
    count += 1;
    if (allDirectoriesExpanded && node.kind === "directory") {
      count += countVisibleTurnDiffTreeRows(node.children, allDirectoriesExpanded);
    }
  }
  return count;
}

export function estimateChangedFilesSummaryHeight(
  files: ReadonlyArray<TurnDiffFileChange>,
  allDirectoriesExpanded = true,
): number {
  if (files.length === 0) return 0;

  const cacheKey = allDirectoriesExpanded ? "expanded" : "collapsed";
  const cachedHeights = changedFilesSummaryHeightCache.get(files);
  const cachedHeight = cachedHeights?.[cacheKey];
  if (typeof cachedHeight === "number") {
    return cachedHeight;
  }

  const visibleRowCount = countVisibleTurnDiffTreeRows(
    buildTurnDiffTree(files),
    allDirectoriesExpanded,
  );

  const height =
    TURN_DIFF_SUMMARY_CHROME_HEIGHT_PX +
    visibleRowCount * TURN_DIFF_TREE_ROW_HEIGHT_PX +
    Math.max(visibleRowCount - 1, 0) * TURN_DIFF_TREE_ROW_GAP_PX;
  changedFilesSummaryHeightCache.set(files, {
    ...cachedHeights,
    [cacheKey]: height,
  });

  return height;
}

// Count wrapped lines without walking a whole 16 KB tool output: the detail panel
// scrolls past its max height anyway, so stop as soon as the cap is reached.
function estimateBoundedLineCount(text: string, charsPerLine: number, maxLines: number): number {
  let lines = 0;
  let currentLineLength = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (text.charCodeAt(index) === 10) {
      lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
      currentLineLength = 0;
      if (lines >= maxLines) {
        return maxLines;
      }
      continue;
    }
    currentLineLength += 1;
    if (lines + Math.ceil(currentLineLength / charsPerLine) >= maxLines) {
      return maxLines;
    }
  }
  return Math.min(lines + Math.max(1, Math.ceil(currentLineLength / charsPerLine)), maxLines);
}

// Level 3: the panel with the `$ command` line and the real output.
export function estimateTimelineWorkEntryDetailHeight(entry: TimelineWorkEntryHeightInput): number {
  const output = entry.output ?? "";
  const maxOutputLines = Math.ceil(
    WORK_ENTRY_DETAIL_MAX_HEIGHT_PX / WORK_ENTRY_DETAIL_LINE_HEIGHT_PX,
  );
  const outputLines =
    output.length > 0
      ? estimateBoundedLineCount(output, WORK_ENTRY_DETAIL_CHARS_PER_LINE, maxOutputLines)
      : 0;
  const commandLines = entry.command ? 1 : 0;
  // Subagent panel: the status line, then the "Prompt" label plus the prompt
  // itself, then the row of "Open thread" pills, then the "Result" label when its
  // report already arrived.
  const subagentPrompt = entry.subagentTask?.prompt ?? "";
  const subagentLines = entry.subagentTask
    ? 1 +
      (subagentPrompt.length > 0
        ? 1 +
          estimateBoundedLineCount(subagentPrompt, WORK_ENTRY_DETAIL_CHARS_PER_LINE, maxOutputLines)
        : 0) +
      ((entry.subagentTask.threadLinks?.length ?? 0) > 0
        ? SUBAGENT_THREAD_LINKS_LINE_EQUIVALENT
        : 0) +
      (outputLines > 0 ? 1 : 0)
    : 0;
  if (outputLines === 0 && commandLines === 0 && subagentLines === 0) {
    return 0;
  }

  return (
    WORK_ENTRY_DETAIL_MARGIN_TOP_PX +
    Math.min(
      WORK_ENTRY_DETAIL_CHROME_HEIGHT_PX +
        (outputLines + commandLines + subagentLines) * WORK_ENTRY_DETAIL_LINE_HEIGHT_PX,
      WORK_ENTRY_DETAIL_MAX_HEIGHT_PX,
    )
  );
}

/**
 * Height of the capture previews a local action uploaded, which sit under the
 * row itself and not inside its collapsible detail — so this counts for a
 * collapsed row too.
 *
 * Runs per row on every size estimate, hence the substring guard before the
 * line-by-line parse: `extractWorkEntryAttachmentImages` bails on it as well,
 * and almost no tool output mentions an attachment at all.
 */
export function estimateWorkEntryAttachmentImagesHeight(
  entry: TimelineWorkEntryHeightInput,
): number {
  const output = entry.output;
  if (!output || !output.includes("/attachments/")) return 0;
  const previews = extractWorkEntryAttachmentImages(output).length;
  if (previews === 0) return 0;

  const lines = Math.ceil(previews / WORK_ENTRY_ATTACHMENT_PREVIEWS_PER_LINE);
  return (
    WORK_ENTRY_ATTACHMENT_BLOCK_MARGIN_TOP_PX +
    lines *
      (WORK_ENTRY_ATTACHMENT_PREVIEW_MAX_HEIGHT_PX + WORK_ENTRY_ATTACHMENT_CAPTION_HEIGHT_PX) +
    Math.max(lines - 1, 0) * WORK_ENTRY_ATTACHMENT_GAP_PX
  );
}

function estimateTimelineWorkEntryHeight(entry: TimelineWorkEntryHeightInput): number {
  const hasChangedFiles = (entry.changedFiles?.length ?? 0) > 0;
  const previewIsChangedFiles = hasChangedFiles && !entry.command && !entry.detail;

  return (
    WORK_ENTRY_ROW_HEIGHT_PX +
    (hasChangedFiles && !previewIsChangedFiles ? WORK_ENTRY_CHANGED_FILES_HEIGHT_PX : 0) +
    estimateWorkEntryAttachmentImagesHeight(entry) +
    (entry.expanded ? estimateTimelineWorkEntryDetailHeight(entry) : 0)
  );
}

function visibleWorkEntries(
  entries: ReadonlyArray<TimelineWorkEntryHeightInput>,
  options: TimelineWorkGroupEstimateOptions,
  overflowSide: "head" | "tail",
): ReadonlyArray<TimelineWorkEntryHeightInput> {
  const cap = options.maxVisibleEntries;
  if (cap === undefined || entries.length <= cap) {
    return entries;
  }
  return overflowSide === "tail" ? entries.slice(-cap) : entries.slice(0, cap);
}

// Bias work-log estimates upward so fast scrolls do not stack rows before they are measured.
export function estimateTimelineWorkGroupHeight(
  entries: ReadonlyArray<TimelineWorkEntryHeightInput>,
  options: TimelineWorkGroupEstimateOptions,
): number {
  if (entries.length === 0) return WORK_GROUP_CHROME_HEIGHT_PX;

  // Level 1: the whole run is a single summary line with its chevron.
  if (!options.expanded) {
    return WORK_GROUP_CHROME_HEIGHT_PX + WORK_GROUP_HEADER_HEIGHT_PX;
  }

  // Level 2 (and 3 for whichever rows are open): the summary line stays as the toggle.
  const rows = visibleWorkEntries(entries, options, "tail");
  return (
    WORK_GROUP_CHROME_HEIGHT_PX +
    WORK_GROUP_HEADER_HEIGHT_PX +
    rows.reduce((total, entry) => total + estimateTimelineWorkEntryHeight(entry), 0) +
    Math.max(rows.length - 1, 0) * WORK_ENTRY_GAP_PX
  );
}

// Estimate the inline tool preview block that can appear under assistant messages.
export function estimateTimelineInlineToolPreviewHeight(
  entries: ReadonlyArray<TimelineWorkEntryHeightInput>,
  options: TimelineWorkGroupEstimateOptions,
): number {
  if (entries.length === 0) return 0;

  if (!options.expanded) {
    return INLINE_TOOL_PREVIEW_MARGIN_TOP_PX + INLINE_TOOL_PREVIEW_ROW_HEIGHT_PX;
  }

  const rows = visibleWorkEntries(entries, options, "head");
  return (
    INLINE_TOOL_PREVIEW_MARGIN_TOP_PX +
    INLINE_TOOL_PREVIEW_CONTAINER_CHROME_HEIGHT_PX +
    INLINE_TOOL_PREVIEW_ROW_HEIGHT_PX +
    rows.reduce(
      (total, entry) =>
        total +
        INLINE_TOOL_PREVIEW_ROW_HEIGHT_PX +
        // Same rows, same previews: the inline block renders `SimpleWorkEntryRow`
        // too, so leaving this out here would just move the jump to the tool
        // calls hanging off an assistant message.
        estimateWorkEntryAttachmentImagesHeight(entry) +
        (entry.expanded ? estimateTimelineWorkEntryDetailHeight(entry) : 0),
      0,
    ) +
    rows.length * INLINE_TOOL_PREVIEW_ROW_GAP_PX
  );
}

/**
 * Same wrapped-line count as `estimateWrappedLineCount`, but treats every
 * `` `inline code` `` span as if it were wider (monospace inline code renders
 * wider than the surrounding prose, so a naive char count would undercount its
 * wrap). Folded into one pass over `text` rather than building an expanded
 * copy of it first: the previous version ran a `replace()` that allocated an
 * `"x".repeat(...)` string per code span plus a second full-string copy, both
 * of which are pure garbage once the line count is computed.
 */
function estimateAssistantWrappedLineCount(text: string, charsPerLine: number): number {
  if (text.length === 0) return 1;

  let lines = 0;
  let currentLineLength = 0;
  let cursor = 0;
  INLINE_CODE_SPAN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_CODE_SPAN_REGEX.exec(text)) !== null) {
    const matchStart = match.index;
    const code = match[1] ?? "";
    // Consume the plain text before this code span exactly like
    // estimateWrappedLineCount does, character by character.
    for (let index = cursor; index < matchStart; index += 1) {
      if (text.charCodeAt(index) === 10) {
        lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
        currentLineLength = 0;
        continue;
      }
      currentLineLength += 1;
    }
    // The span itself cannot contain a newline (excluded by the regex), so it
    // only ever inflates the current line's length.
    const inflatedLength = Math.max(
      code.length + 2,
      Math.ceil(
        code.length * ASSISTANT_INLINE_CODE_WIDTH_MULTIPLIER +
          ASSISTANT_INLINE_CODE_WRAP_OVERHEAD_CHARS,
      ),
    );
    currentLineLength += inflatedLength;
    cursor = matchStart + match[0].length;
  }

  for (let index = cursor; index < text.length; index += 1) {
    if (text.charCodeAt(index) === 10) {
      lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
      currentLineLength = 0;
      continue;
    }
    currentLineLength += 1;
  }

  lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
  return lines;
}

export function estimateTimelineMessageHeight(
  message: TimelineMessageHeightInput,
  layout: TimelineHeightEstimateLayout = { timelineWidthPx: null },
): number {
  const chatFontSizePx = normalizeChatFontSizePx(
    layout.chatFontSizePx ?? DEFAULT_CHAT_FONT_SIZE_PX,
  );
  const lineHeightPx = getChatTranscriptLineHeightPx(chatFontSizePx);

  if (message.role === "assistant") {
    const charsPerLine = estimateCharsPerLineForAssistant(layout.timelineWidthPx, chatFontSizePx);
    const estimatedLines = estimateAssistantWrappedLineCount(message.text, charsPerLine);
    const changedFilesHeight = estimateChangedFilesSummaryHeight(
      message.diffSummaryFiles ?? [],
      message.diffSummaryAllDirectoriesExpanded ?? true,
    );
    const inlineToolPreviewHeight = estimateTimelineInlineToolPreviewHeight(
      message.inlineToolEntries ?? [],
      { expanded: message.inlineToolExpanded ?? false },
    );
    return (
      ASSISTANT_BASE_HEIGHT_PX +
      estimatedLines * lineHeightPx +
      changedFilesHeight +
      inlineToolPreviewHeight
    );
  }

  if (message.role === "user") {
    const charsPerLine = estimateCharsPerLineForUser(layout.timelineWidthPx, chatFontSizePx);
    const displayedUserMessage = deriveDisplayedUserMessageState(message.text, {
      hideImageOnlyBootstrapPrompt: (message.attachments?.length ?? 0) > 0,
    });
    const renderedText =
      displayedUserMessage.contexts.length > 0
        ? [
            buildInlineTerminalContextText(displayedUserMessage.contexts),
            displayedUserMessage.visibleText,
          ]
            .filter((part) => part.length > 0)
            .join(" ")
        : displayedUserMessage.visibleText;
    const estimatedLines =
      renderedText.length > 0 ? estimateWrappedLineCount(renderedText, charsPerLine) : 0;
    const attachmentCount = message.attachments?.length ?? 0;
    const attachmentHeight =
      attachmentCount > 0
        ? Math.ceil(attachmentCount / USER_ATTACHMENT_THUMBNAILS_PER_ROW) *
            USER_ATTACHMENT_THUMBNAIL_SIZE_PX +
          Math.max(Math.ceil(attachmentCount / USER_ATTACHMENT_THUMBNAILS_PER_ROW) - 1, 0) *
            USER_ATTACHMENT_THUMBNAIL_GAP_PX +
          (renderedText.length > 0 ? USER_ATTACHMENT_ROW_MARGIN_BOTTOM_PX : 0)
        : 0;
    return USER_BASE_HEIGHT_PX + estimatedLines * lineHeightPx + attachmentHeight;
  }

  // A `system` message renders as SystemNoticeRow: one compact meta-sized line
  // with an icon, not an assistant answer. Estimating it as the latter reserved
  // roughly twice the height it takes, and the surplus showed up as a blank gap
  // above and below the notice.
  const noticeFontSizePx = getAppTypographyScale(chatFontSizePx).chatMetaPx;
  // The reserved characters come off the line width rather than being appended
  // to the text: `estimateSize` runs on every scroll frame and must not allocate.
  const noticeCharsPerLine = Math.max(
    MIN_SYSTEM_NOTICE_CHARS_PER_LINE,
    estimateCharsPerLineForSystemNotice(layout.timelineWidthPx, noticeFontSizePx) -
      SYSTEM_NOTICE_TRAILING_META_CHARS,
  );
  const noticeLines = estimateWrappedLineCount(message.text, noticeCharsPerLine);
  return (
    SYSTEM_NOTICE_CHROME_HEIGHT_PX +
    noticeLines * Math.round(noticeFontSizePx * SYSTEM_NOTICE_LINE_HEIGHT_RATIO)
  );
}
